'use client';

import {
  CaretDown,
  CaretLeft,
  Factory,
  Function as FunctionIcon,
  GearSix,
  Leaf,
  Truck,
  Wrench,
} from '@phosphor-icons/react';
import { useState } from 'react';

import { cn } from '@skrym/shared/utils/cn';

import Chip, { type ChipProps, type TailwindColor } from './chip';

interface RoadmapIconProps {
  Name: string;
  BackgroundColor?: string;
  LogoColor?: string;
  isComplement?: boolean;
}

function RoadmapIcon(props: RoadmapIconProps) {
  const { Name, isComplement } = props;

  if (Name === 'Optimization') {
    return (
      <div className="bg-skrym_yellow p-1 rounded-md">
        <FunctionIcon weight="bold" color="black" />
      </div>
    );
  }
  if (Name === 'TMS') {
    return (
      <div className="bg-skrym_orange p-1 rounded-md">
        <Truck weight="bold" color="black" />
      </div>
    );
  }
  if (Name === 'Emissions') {
    return (
      <div className="bg-skrym_success p-1 rounded-md">
        <Leaf weight="bold" color="black" />
      </div>
    );
  }
  if (Name === 'WMS') {
    return (
      <div className="bg-neutral-800 text-skrym_yellow p-1 rounded-md">
        <Factory weight="bold" />
      </div>
    );
  }
  if (isComplement === true) {
    return (
      <div className="p-1 rounded-md">
        <GearSix weight="bold" />
      </div>
    );
  }
  return (
    <div className="bg-skrym_orange p-1 rounded-md">
      <Factory weight="bold" color="black" />
    </div>
  );
}

interface FeatureStatus {
  Version: 'Alpha' | 'Beta' | string;
  State: 'Analyze' | 'Design' | 'Develop' | 'Test' | 'Deploy' | 'Maintain';
  Color?: string;
  TextOverride?: string;
}

interface Feature {
  Name: string;
  Link: string;
  Status: FeatureStatus;
}

interface ProductArea {
  Name: string;
  Description: string;
  FlexStart?: number;
  FlexEnd?: number;
  Why: string;
  isComplement?: boolean;
  Features: Feature[];
}

interface RoadmapProps {
  productAreas: ProductArea[];
}

const countFeaturesByStatus = (features: Feature[]) => {
  const statusCounts: { [key: string]: number } = {
    Maintain: 0,
    Deploy: 0,
    Test: 0,
    Develop: 0,
    Design: 0,
    Analyze: 0,
  };

  features.forEach((feature) => {
    statusCounts[feature.Status.State]++;
  });

  const sortedStatusCounts = Object.keys(statusCounts)
    .sort((a, b) => getFeatureOrder(b) - getFeatureOrder(a))
    .reduce(
      (obj, key) => {
        obj[key] = statusCounts[key];
        return obj;
      },
      {} as { [key: string]: number },
    );

  return sortedStatusCounts;
};

const getFeatureChipProps = (feature: Feature): ChipProps => {
  let color: TailwindColor;
  let icon: string;
  let dashed = false;
  switch (feature.Status.State) {
    case 'Maintain':
      color = 'purple';
      icon = 'Check';
      break;
    case 'Deploy':
      color = 'green';
      icon = 'RocketLaunch';
      break;
    case 'Develop':
      color = 'pink';
      icon = 'Code';
      break;
    case 'Design':
      color = 'gray';
      icon = 'PencilSimple';
      break;
    case 'Test':
      color = 'orange';
      icon = 'TestTube';
      break;
    case 'Analyze':
      color = 'gray';
      icon = 'LightbulbFilament';
      dashed = true;
      break;
    default:
      color = 'gray';
      icon = 'CheckCircle';
  }

  return {
    color,
    icon,
    dashed,
    label: feature.Status.TextOverride ? feature.Status.TextOverride : feature.Status.Version,
  };
};

const renderFeatureCard = (feature: Feature) => {
  return (
    <a
      href={feature.Link}
      target="_blank"
      rel="noPlanneder noreferrer"
      className="mt-1 mb-1 border rounded-lg border-neutral-200 dark:border-neutral-800 p-1 dark:hover:border-neutral-400 dark:hover:bg-neutral-900 transition-all ease-in duration-150 cursor-pointer hover:border-black flex items-center justify-start no-underline hover:no-underline"
    >
      <div className="font-medium px-2 py-1 rounded-xl text-xs mr-2">{feature.Name}</div>
      <Chip {...getFeatureChipProps(feature)} />
    </a>
  );
};

const getFeatureOrder = (state: string) => {
  switch (state) {
    case 'Maintain':
      return 1;
    case 'Deploy':
      return 2;
    case 'Test':
      return 3;
    case 'Develop':
      return 4;
    case 'Design':
      return 5;
    default:
      return 6;
  }
};

const renderFeatures = (features: Feature[]) => {
  return (
    <div className="flex flex-wrap gap-1 ">
      {features
        .sort((a, b) => getFeatureOrder(a.Status.State) - getFeatureOrder(b.Status.State))
        .map((feature) => renderFeatureCard(feature))}
    </div>
  );
};

function Roadmap(props: RoadmapProps) {
  const { productAreas } = props;

  const [hoveredProductArea, setHoveredProductArea] = useState<string>();
  const [selectedProductAreas, setSelectedProductAreas] = useState<string[]>([]);
  const [isToolboxExpanded, setIsToolboxExpanded] = useState<boolean>(false);

  const [expandedWhy, setExpandedWhy] = useState<string[]>([]);
  const [expandedFeatures, setExpandedFeatures] = useState<string[]>([]);

  const handleClick = (productAreaName: string) => {
    if (selectedProductAreas.includes(productAreaName)) {
      setSelectedProductAreas(selectedProductAreas.filter((area) => area !== productAreaName)); // Deselect if already selected
    } else {
      setSelectedProductAreas([...selectedProductAreas, productAreaName]); // Add new selection
    }
  };

  const handleWhyClick = (productAreaName: string) => {
    if (expandedWhy.includes(productAreaName)) {
      setExpandedWhy(expandedWhy.filter((area) => area !== productAreaName)); // Deselect if already selected
    } else {
      setExpandedWhy([...expandedWhy, productAreaName]); // Add new selection
    }
  };

  const handleFeaturesClick = (productAreaName: string) => {
    if (expandedFeatures.includes(productAreaName)) {
      setExpandedFeatures(expandedFeatures.filter((area) => area !== productAreaName)); // Deselect if already selected
    } else {
      setExpandedFeatures([...expandedFeatures, productAreaName]); // Add new selection
    }
  };

  const handleToolboxClick = () => {
    setIsToolboxExpanded(!isToolboxExpanded); // Toggle Toolbox
    setSelectedProductAreas([]); // Unselect all product areas
  };

  const renderProductAreas = () => {
    if (!productAreas) return null;

    return productAreas.map((productArea) => {
      const featureCounts = countFeaturesByStatus(productArea.Features);

      return (
        // biome-ignore lint/a11y/noStaticElementInteractions: when touching this code, please fix this lint issue or change this comment to explain why it should be kept
        <div
          key={productArea.Name}
          className={cn(
            'border rounded-lg dark:hover:bg-neutral-900 mt-2 mb-2 border-neutral-200 dark:border-neutral-800 dark:hover:border-neutral-700 hover:border-neutral-400 transition-all ease-in duration-150 hover:-translate-y-1 hover:shadow-md cursor-pointer',
            productArea.isComplement ? 'py-2 pl-3' : 'p-4',
          )}
          style={{
            marginLeft: `${productArea.FlexStart ? productArea.FlexStart * 10 : 0}%`,
            marginRight: `${productArea.FlexEnd ? productArea.FlexEnd * 10 : 0}%`,
          }}
          onMouseEnter={() => setHoveredProductArea(productArea.Name)}
          onMouseLeave={() => setHoveredProductArea(undefined)}
          onClick={() => handleClick(productArea.Name)}
          onKeyDown={() => handleClick(productArea.Name)}
        >
          <div className="flex items-center justify-between">
            <div className="flex gap-.5 items-center">
              <RoadmapIcon Name={productArea.Name} isComplement={productArea.isComplement} />
              <span className={cn('font-bold uppercase px-2 py-1 rounded-xl text-sm mr-2')}>
                {productArea.Name}
              </span>
            </div>
            {(hoveredProductArea === productArea.Name ||
              selectedProductAreas.includes(productArea.Name)) && (
              <div
                className={cn('flex gap-1 flex-wrap', productArea.isComplement === true && 'mr-3')}
              >
                {Object.keys(featureCounts).map((status) => (
                  <div key={status} title={`${featureCounts[status]} Features are ${status}`}>
                    <Chip
                      color={
                        getFeatureChipProps({
                          Name: '',
                          Link: '',
                          Status: {
                            Version: '',
                            State: status as
                              | 'Maintain'
                              | 'Deploy'
                              | 'Develop'
                              | 'Design'
                              | 'Analyze'
                              | 'Test',
                          },
                        }).color
                      }
                      label={`${featureCounts[status]}`}
                      icon={
                        getFeatureChipProps({
                          Name: '',
                          Link: '',
                          Status: {
                            Version: '',
                            State: status as
                              | 'Maintain'
                              | 'Deploy'
                              | 'Develop'
                              | 'Design'
                              | 'Analyze'
                              | 'Test',
                          },
                        }).icon
                      }
                      dashed={
                        getFeatureChipProps({
                          Name: '',
                          Link: '',
                          Status: {
                            Version: '',
                            State: status as
                              | 'Maintain'
                              | 'Deploy'
                              | 'Develop'
                              | 'Design'
                              | 'Analyze'
                              | 'Test',
                          },
                        }).dashed
                      }
                      key={status}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedProductAreas.includes(productArea.Name) && (
            <div className="mt-2 text-sm">
              {productArea.Description}
              <div className="flex flex-col">
                <div className="inline-flex items-center mt-2">
                  {/** biome-ignore lint/a11y/noStaticElementInteractions: when touching this code, please fix this lint issue or change this comment to explain why it should be kept */}
                  <span
                    className="flex gap-1 items-center font-semibold tracking-wider uppercase text-xs text-neutral-500 dark:hover:bg-neutral-800 hover:bg-neutral-100 rounded-xs p-1 cursor-pointer"
                    onClick={(event) => {
                      handleWhyClick(productArea.Name);
                      event?.stopPropagation();
                    }}
                    onKeyDown={(event) => {
                      handleWhyClick(productArea.Name);
                      event?.stopPropagation();
                    }}
                  >
                    Why
                    {expandedWhy.includes(productArea.Name) ? (
                      <CaretDown weight="bold" />
                    ) : (
                      <CaretLeft weight="bold" />
                    )}
                  </span>
                </div>
                {expandedWhy.includes(productArea.Name) && productArea.Why}
                <div className="inline-flex items-start mt-2">
                  {/** biome-ignore lint/a11y/noStaticElementInteractions: when touching this code, please fix this lint issue or change this comment to explain why it should be kept */}
                  <span
                    className="flex items-center gap-1 font-semibold tracking-wider uppercase text-xs text-neutral-500 dark:hover:bg-neutral-800 hover:bg-neutral-100 rounded-xs p-1 cursor-pointer"
                    onClick={(event) => {
                      handleFeaturesClick(productArea.Name);
                      event?.stopPropagation();
                    }}
                    onKeyDown={(event) => {
                      handleFeaturesClick(productArea.Name);
                      event?.stopPropagation();
                    }}
                  >
                    Features
                    {expandedFeatures.includes(productArea.Name) ? (
                      <CaretDown weight="bold" />
                    ) : (
                      <CaretLeft weight="bold" />
                    )}
                  </span>
                </div>
                {expandedFeatures.includes(productArea.Name) &&
                  renderFeatures(productArea.Features)}
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div>
      <div className={'grid gap-2 grid-cols-7'}>
        <div className={cn('overflow-auto', isToolboxExpanded ? 'col-span-3' : 'col-span-6')}>
          {productAreas && renderProductAreas()}
        </div>
        {/** biome-ignore lint/a11y/noStaticElementInteractions: when touching this code, please fix this lint issue or change this comment to explain why it should be kept */}
        <div
          className={cn(
            'px-2 py-2 mt-2 mb-2 border rounded-lg border-neutral-200 dark:border-neutral-800 dark:hover:border-neutral-400 dark:hover:bg-neutral-900 transition-all ease-in duration-150 hover:-translate-y-1 hover:shadow-md flex flex-col items-center justify-center cursor-pointer',
            isToolboxExpanded ? 'col-span-4' : 'col-span-1',
          )}
          onClick={handleToolboxClick}
          onKeyDown={handleToolboxClick}
        >
          <div className={cn({ '-rotate-90': !isToolboxExpanded }, 'flex items-center')}>
            <Wrench weight="bold" />
            <span className="font-bold uppercase px-2 py-1 rounded-xl text-sm mr-2">Toolbox</span>
          </div>
          {isToolboxExpanded && (
            <span className="mt-2 text-sm px-2">
              Logistics can differ a lot between seemingly similar organizations. By breaking up our
              product into tools that we can sell individually, we get maximum flexibility to meet
              our users needs. It also gives us many different paths to approach potential new
              users, since not everyone may prioritize the same things at the same time.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default Roadmap;
